import {
  LayoutDashboard,
  Users,
  UserPlus,
  Megaphone,
  ChartLine,
  Radar,
  Sparkles,
  Shield,
  type LucideIcon,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export const APP_NAME = 'Rafobase'

export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  ANALYTICS: '/analytics',
  MEMBERS: '/members',
  REFERRALS: '/referrals',
  REFERRAL_NETWORK: '/referrals/network',
  CREATORS: '/creators',
  CREATORS_ANALYTICS: '/creators/analytics',
  CAMPAIGNS: '/campaigns',
  WEBHOOKS: '/webhooks',
  SETTINGS: '/settings',
  SOCIAL_LISTENING: '/social-listening',
  CREATORS_MODERACAO: '/creators/moderacao',
  PLAYGROUND: '/playground',
  LOGIN: '/login',
  BACKOFFICE_MERCHANTS: '/backoffice/merchants',
  WEBHOOKS_INCOMING: '/webhooks/incoming',
  WEBHOOKS_OUTBOUND: '/webhooks/outbound',
  INTEGRATIONS: '/integrations',
  INTEGRATIONS_KLAVIYO: '/integrations/klaviyo',
} as const

export type NavItem = {
  title: string
  href: string
  icon: LucideIcon
  items?: { title: string; href: string }[]
}

export type NavGroup = {
  label: string
  items: NavItem[]
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Visão Geral',
    items: [
      { title: 'Dashboard', href: ROUTES.DASHBOARD, icon: LayoutDashboard },
      { title: 'Analytics', href: ROUTES.ANALYTICS, icon: ChartLine },
    ],
  },
  {
    label: 'Indicações',
    items: [
      { title: 'Membros', href: ROUTES.MEMBERS, icon: Users },
      { title: 'Indicações', href: ROUTES.REFERRALS, icon: UserPlus },
      { title: 'Programa', href: ROUTES.CAMPAIGNS, icon: Megaphone },
    ],
  },
  {
    label: 'Creators',
    items: [
      { title: 'Analytics', href: ROUTES.CREATORS_ANALYTICS, icon: ChartLine },
      { title: 'Creators', href: ROUTES.CREATORS, icon: Sparkles },
      { title: 'Moderação', href: ROUTES.CREATORS_MODERACAO, icon: Shield },
      { title: 'Social Listening', href: ROUTES.SOCIAL_LISTENING, icon: Radar },
    ],
  },
]

/**
 * Constantes de UI para skeletons e loading states
 */
export const UI = {
  SKELETON_TABLE_ROWS: 5,
  SKELETON_CARDS: 2,
} as const

/**
 * Status de referral
 */
export const REFERRAL_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected',
} as const

export type ReferralStatus = (typeof REFERRAL_STATUS)[keyof typeof REFERRAL_STATUS]

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  cancelled: 'Cancelado',
  rejected: 'Rejeitado',
}

export const REFERRAL_STATUS_VARIANTS: Record<
  ReferralStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'secondary',
  approved: 'default',
  cancelled: 'destructive',
  rejected: 'destructive',
}

/**
 * Tipos de recompensa
 */
export const REWARD_TYPE = {
  PERCENTAGE: 'percentage',
  FIXED: 'fixed',
} as const

export type RewardType = (typeof REWARD_TYPE)[keyof typeof REWARD_TYPE]

/**
 * Status de membro
 */
export const MEMBER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  BLOCKED: 'blocked',
} as const

export type MemberStatus = (typeof MEMBER_STATUS)[keyof typeof MEMBER_STATUS]

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  blocked: 'Bloqueado',
}

/**
 * Formata valor em reais
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

/**
 * Formata data relativa (ex: "há 3 dias", "há 2 semanas")
 */
export function formatRelativeDate(date: Date | string): string {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: ptBR,
  }).replace('cerca de ', '')
}
